'use client'

import React from 'react'
import { useTranslations } from 'next-intl'

import type { RefundBand } from '@/src/utils/holidays/parseRefundTimeline'

import RefundTimeline, { type RefundSegment } from './refundTimeline'

interface RefundTimelineWrapperProps {
    bands: RefundBand[]
    isMobile: boolean
}

/**
 * Colour follows what comes back, not what is charged, so the track always reads green to red as
 * departure nears: a healthy refund is green, a partial one amber, nothing at all red.
 */
const HEALTHY_REFUND = 50

const severityFor = (refundPercent: number): RefundSegment['severity'] => {
    if (refundPercent >= HEALTHY_REFUND) return 'low'
    if (refundPercent > 0) return 'mid'
    return 'high'
}

const RefundTimelineWrapper: React.FC<RefundTimelineWrapperProps> = ({ bands, isMobile }) => {
    const t = useTranslations('holidaysPage')

    if (!bands.length) return null

    const startLabelFor = (band: RefundBand) =>
        band.fromDay === null ? t('refundFromBooking') : t('refundDaysBefore', { days: band.fromDay })

    // Phones get the whole window spelled out per row, because a narrow track can only fit a
    // truncated boundary and "60 days bef…" answers nothing.
    const rangeLabelFor = (band: RefundBand) => {
        const endsAtDeparture = band.toDay === null || band.toDay === 0
        if (band.fromDay === null && endsAtDeparture) return t('refundRangeAll')
        if (band.fromDay === null) return t('refundRangeFromBooking', { to: band.toDay as number })
        if (endsAtDeparture) return t('refundRangeToDeparture', { from: band.fromDay })
        return t('refundRangeDays', { from: band.fromDay, to: band.toDay as number })
    }

    const segments: RefundSegment[] = bands.map(band => {
        const refundPercent = 100 - band.chargePercent
        return {
            startLabel: startLabelFor(band),
            // Stated as what comes back, which is the question being asked, with the
            // nothing-back case named rather than shown as "0%".
            valueLabel:
                refundPercent <= 0 ? t('refundNonRefundable') : t('refundPercentBack', { percent: refundPercent }),
            rangeLabel: rangeLabelFor(band),
            severity: severityFor(refundPercent)
        }
    })

    // The headline answers the question the table never did: what is the best I can do? It reads
    // off the first band, which is the one furthest from departure and so the cheapest to cancel.
    const best = bands[0]
    const bestRefund = 100 - best.chargePercent
    const headline =
        bestRefund > 0
            ? t('refundHeadlineBest', {
                  when: best.toDay === null ? t('refundDeparture') : t('refundDaysBefore', { days: best.toDay }),
                  percent: bestRefund
              })
            : t('refundHeadlineNone')

    return (
        <RefundTimeline
            cancelBetweenLabel={t('refundCancelBetween')}
            amountRefundableLabel={t('refundAmountRefundable')}
            headline={headline}
            segments={segments}
            isMobile={isMobile}
        />
    )
}

export { RefundTimelineWrapper }
export default RefundTimelineWrapper
